import { useState, useCallback } from 'react';
import type { ProgramWizardStepNumber, ProgramWizardState } from './programWizardTypes';
import type { CertificationType } from '../../../types/program';

export interface ProgramWizardInitialData {
  programId: string;
  name?: string;
  description?: string | null;
  header_image?: string | null;
  duration_days?: number;
  certification_type?: CertificationType | null;
  certificate_file_key?: string | null;
}

function buildInitialState(init?: ProgramWizardInitialData): ProgramWizardState {
  if (!init) {
    return {
      programId: null,
      steps: {
        1: { status: 'active' },
        2: { status: 'pending' },
        3: { status: 'pending' },
      },
    };
  }

  // Edit mode — program already exists, all steps accessible
  return {
    programId: init.programId,
    steps: {
      1: { status: 'saved' },
      2: { status: 'active' },
      3: { status: 'active' },
    },
  };
}

export function useProgramWizardState(initialData?: ProgramWizardInitialData) {
  const [state, setState] = useState<ProgramWizardState>(() => buildInitialState(initialData));

  const setProgramId = useCallback((id: string) => {
    setState((prev) => ({
      ...prev,
      programId: id,
      steps: {
        ...prev.steps,
        // Unlock steps 2-3 once programId exists
        2: { status: prev.steps[2].status === 'pending' ? 'active' : prev.steps[2].status },
        3: { status: prev.steps[3].status === 'pending' ? 'active' : prev.steps[3].status },
      },
    }));
  }, []);

  const markStepSaved = useCallback((step: ProgramWizardStepNumber) => {
    setState((prev) => ({
      ...prev,
      steps: {
        ...prev.steps,
        [step]: { status: 'saved' },
      },
    }));
  }, []);

  const markStepActive = useCallback((step: ProgramWizardStepNumber) => {
    setState((prev) => ({
      ...prev,
      steps: {
        ...prev.steps,
        [step]: { status: 'active' },
      },
    }));
  }, []);

  const isStepEnabled = useCallback(
    (step: ProgramWizardStepNumber): boolean => {
      if (step === 1) return true;
      return state.programId !== null;
    },
    [state.programId]
  );

  const savedStepCount = Object.values(state.steps).filter((s) => s.status === 'saved').length;
  const allStepsSaved = savedStepCount === 3;

  return {
    state,
    setProgramId,
    markStepSaved,
    markStepActive,
    isStepEnabled,
    allStepsSaved,
    savedStepCount,
  };
}

export type UseProgramWizardStateReturn = ReturnType<typeof useProgramWizardState>;
